import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { parseLocalYmd, weekdayNames, type Plan } from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import { formatDayMonth } from "../Logger/formatDate";
import type { PlansResourceStatus } from "../Plans/usePlans";
import { Button } from "../primitives/Button";
import { useLocale } from "../primitives/useLocale";
import { webTapFloor, type UiTheme } from "../theme";

// #368: the plans of the day tapped on the diary's calendar. A row opens the
// plan in its editor; Start, where the host has a logger, starts it, as the
// Library's plan card does. Under the grid on the phone, in the detail pane
// on the wide web.
export function PlannedDayList({
  theme,
  day,
  plans,
  status,
  onOpenPlan,
  onStartPlan,
  startingPlanId,
}: {
  theme: UiTheme;
  /** "YYYY-MM-DD". */
  day: string;
  /** That day's plans. */
  plans: readonly Plan[];
  status: PlansResourceStatus;
  onOpenPlan: (plan: Plan) => void;
  onStartPlan?: (plan: Plan) => void;
  startingPlanId?: string | null;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const styles = makeStyles(theme);
  const date = parseLocalYmd(day);
  const when = date ? `${weekdayNames(locale, 0)[date.getDay()]} ${formatDayMonth(date, locale)}` : day;
  return (
    <View style={styles.wrap}>
      <Text style={styles.head} accessibilityRole="header">
        {when}
        {plans.length > 0
          ? ` · ${t("calendar.planned", { count: plans.length })}`
          : ""}
      </Text>
      {status === "loading" ? (
        <ActivityIndicator color={theme.palette.ink2} accessibilityLabel={t("plans.loading")} />
      ) : status === "error" && plans.length === 0 ? (
        <Text style={styles.muted}>{t("calendar.loadError")}</Text>
      ) : plans.length === 0 ? (
        <Text style={styles.muted}>{t("calendar.nothing")}</Text>
      ) : (
        plans.map((plan) => (
          <View key={plan.id} style={styles.row}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={plan.name}
              accessibilityHint={t("calendar.openPlanHint")}
              onPress={() => onOpenPlan(plan)}
              style={({ pressed }) => [styles.main, pressed && styles.pressed]}
            >
              <View style={styles.flex}>
                <Text style={styles.name} numberOfLines={2}>
                  {plan.name}
                </Text>
                <Text style={styles.muted}>
                  {t("calendar.exercises", { count: plan.entries.length })}
                </Text>
              </View>
              <Icon name="chevron-right" size={theme.typography.sizes.md} color={theme.palette.ink3} />
            </Pressable>
            {onStartPlan ? (
              <Button
                theme={theme}
                variant="secondary"
                compact
                icon="play"
                label={t("plans.start")}
                accessibilityLabel={`${t("plans.start")}, ${plan.name}`}
                onPress={() => onStartPlan(plan)}
                loading={startingPlanId === plan.id}
                disabled={!!startingPlanId && startingPlanId !== plan.id}
              />
            ) : null}
          </View>
        ))
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s2, padding: s.s4 },
    head: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "700" },
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
      paddingRight: s.s3,
    },
    main: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      minHeight: webTapFloor ?? 44,
      paddingVertical: s.s3,
      paddingLeft: s.s3,
    },
    pressed: { opacity: 0.6 },
    flex: { flex: 1, gap: 2 },
    name: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "600" },
    muted: { color: p.ink3, fontSize: ty.sizes.sm },
  });
}
